import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminProfile } from "../types";

export const profile = createQueryKeys("admin-profile", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number | "me") => [id],
});

const include = "user.position";

export type ProfileList = Paginate<AdminProfile>;

export const fetchProfilesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...profile.all(page, search),
    queryFn: () =>
      adminApi
        .get<ProfileList>(`profile/profiles`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchProfiles = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchProfilesQueryOptions(page, search));

export const fetchProfileQueryOptions = (id?: number) =>
  queryOptions({
    ...profile.one(id),
    queryFn: () =>
      adminApi
        .get<AdminProfile<true>>(`profile/profiles/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchProfile = (id?: number) =>
  useQuery(fetchProfileQueryOptions(id));

export interface ProfileUpdateRequest {
  id?: number;
  status: AdminProfile["status"];
  description?: string;
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: ProfileUpdateRequest) =>
      adminApi
        .put(`profile/profiles/${id}`, data)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: profile.all._def,
      });
      queryClient.invalidateQueries(profile.one(id));
    },
  });
};
